import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { Button } from '../ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../ui/dropdown-menu';
import {
  GitBranch, Download, Upload, ChevronDown, Settings, LogOut, User,
  Sun, Moon, Monitor, Shield, FilePlus2, HelpCircle,
} from 'lucide-react';

export function DashboardHeader({
  isGuest, currentAutomaton,
  onShowAuthDialog, onNewAutomaton, onExport, onGuestImportClick,
  onToggleHelp,
}) {
  const navigate = useNavigate();
  const { user, logout, isAdmin } = useAuth();
  const { theme, setTheme } = useTheme();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <header className="h-14 border-b border-border flex items-center justify-between px-4 shrink-0">
      <div className="flex items-center gap-4">
        <Link to={isGuest ? '/editor' : '/dashboard'} className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
            <GitBranch className="w-5 h-5 text-primary-foreground" />
          </div>
          <span className="font-bold hidden sm:inline">AutomataLab</span>
        </Link>

        {currentAutomaton && isGuest && (
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">/</span>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="gap-1" data-testid="automaton-dropdown">
                  {currentAutomaton.name}
                  <ChevronDown className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuItem onClick={() => onNewAutomaton('DFA')} data-testid="new-automaton-dfa-btn">
                  <FilePlus2 className="w-4 h-4 mr-2" /> New DFA/NFA
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onNewAutomaton('PDA')} data-testid="new-automaton-pda-btn">
                  <FilePlus2 className="w-4 h-4 mr-2" /> New PDA
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onNewAutomaton('TM')} data-testid="new-automaton-tm-btn">
                  <FilePlus2 className="w-4 h-4 mr-2" /> New Turing Machine
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={onExport}>
                  <Download className="w-4 h-4 mr-2" /> Export
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onGuestImportClick}>
                  <Upload className="w-4 h-4 mr-2" /> Import
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <span className="text-xs text-muted-foreground hidden sm:block">
              Guest mode — sign in to save
            </span>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" data-testid="theme-toggle">
              {theme === 'dark' ? <Moon className="w-5 h-5" /> :
               theme === 'light' ? <Sun className="w-5 h-5" /> :
               <Monitor className="w-5 h-5" />}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => setTheme('light')}>
              <Sun className="w-4 h-4 mr-2" /> Light
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setTheme('dark')}>
              <Moon className="w-4 h-4 mr-2" /> Dark
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setTheme('system')}>
              <Monitor className="w-4 h-4 mr-2" /> System
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <Button variant="ghost" size="icon" onClick={onToggleHelp} data-testid="help-toggle-btn" title="Help & Reference">
          <HelpCircle className="w-5 h-5" />
        </Button>

        {isGuest ? (
          <Button variant="default" size="sm" className="btn-primary" onClick={onShowAuthDialog} data-testid="sign-in-btn">
            <User className="w-4 h-4 mr-2" /> Sign In
          </Button>
        ) : (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="user-menu">
                {user?.picture ? (
                  <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
                ) : (
                  <User className="w-5 h-5" />
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <div className="px-2 py-1.5">
                <p className="font-medium">{user?.name}</p>
                <p className="text-xs text-muted-foreground">{user?.email}</p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/settings')}>
                <Settings className="w-4 h-4 mr-2" /> Settings
              </DropdownMenuItem>
              {isAdmin && (
                <DropdownMenuItem onClick={() => navigate('/admin')}>
                  <Shield className="w-4 h-4 mr-2" /> Admin
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout}>
                <LogOut className="w-4 h-4 mr-2" /> Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </header>
  );
}
